import type { ReactNode } from 'react';
import type { GraphQLError } from 'graphql';

interface ConfluenceMutationErrorExtensionBase {
	errorType: string | null;
	statusCode: number | null;
}

export type MaybeGraphQLErrors = null | undefined | Partial<GraphQLError>[];

// If there are unique extension argument, we can cast them in the consumer side
export type GraphQLErrorHandler = (
	args: ConfluenceMutationErrorExtensionBase,
) => ReactNode | undefined;

type StatusCodeHandler = {
	[key: number]: string | GraphQLErrorHandler;
};

// We may have something similar to this in the codebase already.
// As a follow-up, we can replace this with existing tooling later.
export const handleGraphQLErrors = (
	errors: MaybeGraphQLErrors,
	statusCodeHandler?: StatusCodeHandler,
) => {
	errors?.forEach((error) => {
		if (!error) {
			return;
		}

		// If the error is coming from GraphQL service, extension is an object as typed in GraphQLError
		// If it's coming from Monolith, it's an array as typed in ConfluenceMutationErrorExtension
		const extension = Array.isArray(error.extensions) ? error.extensions[0] : error.extensions;

		if (extension && extension.statusCode && statusCodeHandler) {
			const errorHandler = statusCodeHandler[extension.statusCode];
			const customErrorMessage =
				typeof errorHandler === 'function' ? errorHandler(extension) : errorHandler;
			// errorHandler(extension) can return a ReactNode. In that case, we put that into "messageRichText".
			const finalErrorMessage =
				typeof customErrorMessage === 'string' ? customErrorMessage : error.message;

			const errorObject = new Error(finalErrorMessage) as Error & {
				isGraphQLError: boolean;
				messageRichText: ReactNode;
			};
			errorObject.isGraphQLError = true;
			errorObject.messageRichText =
				typeof customErrorMessage !== 'string' ? customErrorMessage : undefined;

			throw errorObject;
		}

		if (error.message) {
			const errorObject = new Error(error.message) as Error & {
				isGraphQLError: boolean;
			};
			errorObject.isGraphQLError = true;
			throw errorObject;
		}

		throw error;
	});
};
